<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { executeRefund, listRefunds, updateRefund, type RefundRequest } from "../api/operations";

type RefundStatus = "APPLIED" | "PROCESSING" | "APPROVED" | "REJECTED";
type RefundFilter = "ALL" | RefundStatus;
type RefundRow = RefundRequest & {
  processedAt?: string | null;
};

const statusOptions: Array<{ value: RefundFilter; label: string }> = [
  { value: "ALL", label: "全部" },
  { value: "APPLIED", label: "待审核" },
  { value: "PROCESSING", label: "处理中" },
  { value: "APPROVED", label: "审核通过" },
  { value: "REJECTED", label: "已拒绝" },
];

const statusLabels: Record<string, string> = {
  APPLIED: "待审核",
  PROCESSING: "处理中",
  APPROVED: "审核通过（未退款）",
  REJECTED: "已拒绝",
};
const paymentRefundStatusLabels: Record<string, string> = {
  PENDING: "退款待提交",
  PROCESSING: "退款处理中",
  SUCCESS: "退款成功",
  FAILED: "退款失败",
};

const rows = ref<RefundRow[]>([]);
const loading = ref(true);
const loadError = ref("");
const searchQuery = ref("");
const statusFilter = ref<RefundFilter>("ALL");
const expandedId = ref<string | null>(null);
const actionBusyId = ref<string | null>(null);
const actionSuccess = ref("");
const actionError = ref("");

const money = (value: number): string => `¥${(value / 100).toFixed(2)}`;

function parseApiDate(value: string): Date {
  const normalized = value.trim().replace(" ", "T");
  const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(normalized);
  return new Date(hasTimezone ? normalized : `${normalized}Z`);
}

function formatDate(value: string | null | undefined): string {
  if (!value) return "—";
  const date = parseApiDate(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("zh-CN", {
    timeZone: "Asia/Shanghai",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function statusText(status: string): string {
  return statusLabels[status] ?? status;
}

function paymentRefundStatusText(status: string): string {
  return paymentRefundStatusLabels[status] ?? status;
}

function statusClass(status: string): string {
  return `refund-status--${status.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
}

function messageFrom(errorValue: unknown, fallback: string): string {
  return errorValue instanceof Error ? errorValue.message : fallback;
}

function displayRefundNo(row: RefundRow): string {
  return row.displayRefundNo ?? row.refundNo;
}

function displayOrderNo(row: RefundRow): string {
  return row.displayOrderNo ?? row.orderNo;
}

const filteredRows = computed(() => {
  const query = searchQuery.value.trim().toLocaleLowerCase();

  return rows.value.filter((row) => {
    const searchableText = [
      row.refundNo,
      row.displayRefundNo,
      row.orderNo,
      row.displayOrderNo,
      row.userId,
      row.reason,
      row.status,
      statusText(row.status),
    ]
      .filter(Boolean)
      .join(" ")
      .toLocaleLowerCase();

    const matchesSearch = !query || searchableText.includes(query);
    const matchesStatus = statusFilter.value === "ALL" || row.status === statusFilter.value;
    return matchesSearch && matchesStatus;
  });
});

const summary = computed(() => ({
  applied: rows.value.filter((row) => row.status === "APPLIED").length,
  processing: rows.value.filter((row) => row.status === "PROCESSING").length,
  requestedAmount: rows.value.reduce((total, row) => total + row.requestedAmount, 0),
}));

async function load(): Promise<boolean> {
  loading.value = true;
  loadError.value = "";
  try {
    rows.value = await listRefunds();
    return true;
  } catch (errorValue) {
    loadError.value = messageFrom(errorValue, "退款申请加载失败");
    return false;
  } finally {
    loading.value = false;
  }
}

function toggleDetails(id: string): void {
  expandedId.value = expandedId.value === id ? null : id;
}

function clearFilters(): void {
  searchQuery.value = "";
  statusFilter.value = "ALL";
}

async function updateStatus(id: string, status: "PROCESSING" | "APPROVED" | "REJECTED"): Promise<void> {
  if (actionBusyId.value) return;

  const target = rows.value.find((row) => row.id === id);
  actionBusyId.value = id;
  actionSuccess.value = "";
  actionError.value = "";

  try {
    const refreshed = await updateRefund(id, status);
    const reloaded = await load();
    if (!reloaded) {
      actionError.value = "状态已更新，但列表刷新失败，请稍后重新加载。";
      return;
    }
    actionSuccess.value = `${target ? displayRefundNo(target) : displayRefundNo(refreshed)}已更新为${statusText(status)}`;
  } catch (errorValue) {
    actionError.value = messageFrom(errorValue, "退款申请状态更新失败，请稍后重试");
  } finally {
    actionBusyId.value = null;
  }
}

async function executeApprovedRefund(id: string): Promise<void> {
  if (actionBusyId.value) return;
  const target = rows.value.find((row) => row.id === id);
  if (!target) return;
  const amount = money(target.requestedAmount);
  if (!window.confirm(`确认执行退款 ${amount}？\n该操作将发起真实资金退款。`)) return;
  actionBusyId.value = id;
  actionSuccess.value = "";
  actionError.value = "";
  try {
    await executeRefund(id);
    const reloaded = await load();
    if (!reloaded) {
      actionError.value = "退款已提交，但列表刷新失败，请稍后重新加载。";
      return;
    }
    actionSuccess.value = `${displayRefundNo(target)}已提交实际退款`;
  } catch (errorValue) {
    actionError.value = messageFrom(errorValue, "实际退款执行失败，请稍后重试");
  } finally {
    actionBusyId.value = null;
  }
}

onMounted(() => {
  void load();
});
</script>

<template>
  <section class="refunds-page">
    <header class="refunds-header">
      <div>
        <h1>退款申请管理</h1>
        <p class="refunds-intro">
          处理客户售后退款申请及状态跟踪；审核通过后可在本地测试渠道执行退款。
        </p>
      </div>
      <span class="refunds-loaded">当前已加载 {{ rows.length }} 条申请</span>
    </header>

    <section
      class="refunds-summary"
      aria-label="退款申请当前加载范围统计"
    >
      <article class="refund-metric-card">
        <div class="refund-metric-card__heading">
          <span
            class="refund-metric-card__icon"
            aria-hidden="true"
          >▣</span>
          <span>待审核申请数</span>
        </div>
        <strong>{{ summary.applied }}</strong>
        <small>当前已加载范围</small>
      </article>
      <article class="refund-metric-card refund-metric-card--processing">
        <div class="refund-metric-card__heading">
          <span
            class="refund-metric-card__icon"
            aria-hidden="true"
          >↻</span>
          <span>处理中申请数</span>
        </div>
        <strong>{{ summary.processing }}</strong>
        <small>当前已加载范围</small>
      </article>
      <article class="refund-metric-card refund-metric-card--amount">
        <div class="refund-metric-card__heading">
          <span
            class="refund-metric-card__icon"
            aria-hidden="true"
          >¥</span>
          <span>申请金额合计</span>
        </div>
        <strong>{{ money(summary.requestedAmount) }}</strong>
        <small>当前已加载申请记录合计</small>
      </article>
      <section class="refund-filter-card">
        <span class="refund-filter-card__label">业务状态筛选</span>
        <label class="refunds-control">
          <span class="sr-only">筛选退款申请状态</span>
          <select
            v-model="statusFilter"
            aria-label="筛选退款申请状态"
          >
            <option
              v-for="option in statusOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </label>
      </section>
    </section>

    <section class="refunds-workspace">
      <div class="refunds-toolbar">
        <div>
          <h2>退款申请列表</h2>
          <p>当前列表仅展示接口已返回的真实记录（最多 200 条）。</p>
        </div>
        <label class="refunds-control refunds-search">
          <span>搜索</span>
          <input
            v-model="searchQuery"
            aria-label="搜索当前退款申请"
            placeholder="搜索售后单号、关联订单号或退款原因"
            type="search"
          >
        </label>
      </div>

      <p
        v-if="actionSuccess"
        aria-live="polite"
        class="refunds-feedback refunds-feedback--success"
      >
        {{ actionSuccess }}
      </p>
      <p
        v-if="actionError"
        aria-live="assertive"
        class="refunds-feedback refunds-feedback--error"
      >
        {{ actionError }}
      </p>

      <div
        v-if="loading"
        class="refunds-feedback refunds-feedback--loading"
      >
        正在加载退款申请…
      </div>
      <div
        v-else-if="loadError"
        aria-live="polite"
        class="refunds-feedback refunds-feedback--error"
      >
        {{ loadError }}
        <button
          type="button"
          @click="void load()"
        >
          重新加载
        </button>
      </div>
      <div
        v-else
        class="refunds-table-wrap"
      >
        <table class="refunds-table">
          <thead>
            <tr>
              <th scope="col">
                申请号 / 关联订单
              </th>
              <th scope="col">
                用户
              </th>
              <th scope="col">
                退款原因 / 申请金额
              </th>
              <th scope="col">
                申请时间
              </th>
              <th scope="col">
                状态
              </th>
              <th scope="col">
                操作
              </th>
            </tr>
          </thead>
          <tbody>
            <template
              v-for="row in filteredRows"
              :key="row.id"
            >
              <tr>
                <td class="refund-identity-cell">
                  <strong>{{ displayRefundNo(row) }}</strong>
                  <span>{{ displayOrderNo(row) }}</span>
                </td>
                <td>{{ row.userId }}</td>
                <td class="refund-reason-cell">
                  <span :title="row.reason">{{ row.reason }}</span>
                  <strong>{{ money(row.requestedAmount) }}</strong>
                </td>
                <td>
                  <time :datetime="row.createdAt">{{ formatDate(row.createdAt) }}</time>
                </td>
                <td>
                  <span
                    class="refund-status"
                    :class="statusClass(row.status)"
                  >
                    {{ statusText(row.status) }}
                  </span>
                  <span
                    v-if="row.paymentRefund"
                    class="refund-status refund-status--payment"
                    :class="statusClass(row.paymentRefund.status)"
                  >
                    {{ paymentRefundStatusText(row.paymentRefund.status) }}
                  </span>
                </td>
                <td class="refund-actions">
                  <button
                    class="refund-detail-button"
                    type="button"
                    :aria-expanded="expandedId === row.id"
                    @click="toggleDetails(row.id)"
                  >
                    {{ expandedId === row.id ? "收起详情" : "查看详情" }}
                  </button>
                  <button
                    v-if="row.status === 'APPLIED'"
                    :disabled="actionBusyId !== null"
                    type="button"
                    @click="void updateStatus(row.id, 'PROCESSING')"
                  >
                    {{ actionBusyId === row.id ? "处理中…" : "标记处理中" }}
                  </button>
                  <button
                    v-if="row.status === 'APPLIED' || row.status === 'PROCESSING'"
                    :disabled="actionBusyId !== null"
                    type="button"
                    @click="void updateStatus(row.id, 'REJECTED')"
                  >
                    拒绝
                  </button>
                  <button
                    v-if="row.status === 'PROCESSING'"
                    :disabled="actionBusyId !== null"
                    type="button"
                    @click="void updateStatus(row.id, 'APPROVED')"
                  >
                    审核通过
                  </button>
                  <button
                    v-if="row.status === 'APPROVED' && row.paymentRefund?.status !== 'SUCCESS'"
                    :disabled="actionBusyId !== null"
                    type="button"
                    @click="void executeApprovedRefund(row.id)"
                  >
                    {{ actionBusyId === row.id ? "退款中…" : "执行退款" }}
                  </button>
                </td>
              </tr>
              <tr
                v-if="expandedId === row.id"
                class="refund-detail-row"
              >
                <td colspan="6">
                  <dl class="refund-detail-grid">
                    <div>
                      <dt>申请号</dt>
                      <dd>{{ displayRefundNo(row) }}</dd>
                    </div>
                    <div>
                      <dt>订单号</dt>
                      <dd>{{ displayOrderNo(row) }}</dd>
                    </div>
                    <div>
                      <dt>用户</dt>
                      <dd>{{ row.userId }}</dd>
                    </div>
                    <div>
                      <dt>申请金额</dt>
                      <dd>{{ money(row.requestedAmount) }}</dd>
                    </div>
                    <div class="refund-detail-grid__wide">
                      <dt>退款原因</dt>
                      <dd>{{ row.reason }}</dd>
                    </div>
                    <div class="refund-detail-grid__wide">
                      <dt>管理员备注</dt>
                      <dd>{{ row.adminRemark || "—" }}</dd>
                    </div>
                    <div>
                      <dt>处理时间</dt>
                      <dd>{{ formatDate(row.processedAt) }}</dd>
                    </div>
                    <div>
                      <dt>退款状态</dt>
                      <dd>{{ row.paymentRefund ? paymentRefundStatusText(row.paymentRefund.status) : "尚未执行" }}</dd>
                    </div>
                    <div v-if="row.paymentRefund?.refundedAmount != null">
                      <dt>已退金额</dt>
                      <dd>{{ money(row.paymentRefund.refundedAmount) }}</dd>
                    </div>
                  </dl>
                </td>
              </tr>
            </template>
            <tr v-if="!filteredRows.length">
              <td
                class="refunds-empty"
                colspan="6"
              >
                <strong>{{ rows.length ? "没有匹配的退款申请" : "当前没有退款申请" }}</strong>
                <p>{{ rows.length ? "请调整搜索词或状态筛选。" : "当前接口没有返回退款申请记录。" }}</p>
                <button
                  v-if="searchQuery || statusFilter !== 'ALL'"
                  type="button"
                  @click="clearFilters"
                >
                  清除筛选
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <footer class="refunds-table-footer">
          <span>显示 {{ filteredRows.length }} / {{ rows.length }} 条申请</span>
          <span>审核通过仅代表申请状态；退款成功以后才更新订单退款状态</span>
        </footer>
      </div>
    </section>
  </section>
</template>

<style scoped>
.refunds-page {
  display: grid;
  gap: 24px;
  width: calc(100% - 48px);
  max-width: 1440px;
  margin: 0 auto;
  padding: 36px 0 136px;
  color: var(--admin-ink);
}

.refunds-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 28px;
}

.refunds-header h1 {
  margin: 0 0 8px;
  font-size: clamp(32px, 3vw, 42px);
  letter-spacing: -0.04em;
  line-height: 1.1;
}

.refunds-intro,
.refunds-toolbar p {
  margin: 0;
  color: var(--admin-muted);
  font-size: 14px;
  line-height: 1.65;
}

.refunds-loaded {
  padding-bottom: 4px;
  color: var(--admin-muted);
  font-size: 13px;
  white-space: nowrap;
}

.refunds-summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr)) minmax(220px, 0.9fr);
  gap: 16px;
}

.refund-metric-card,
.refund-filter-card {
  min-width: 0;
  min-height: 154px;
  padding: 22px 24px;
  border: 1px solid var(--admin-outline-soft);
  border-radius: 16px;
  background: var(--admin-surface);
  box-shadow: 0 4px 18px rgba(28, 67, 46, 0.04);
}

.refund-metric-card {
  display: grid;
  align-content: space-between;
  gap: 14px;
}

.refund-metric-card--processing {
  border-color: rgba(60, 179, 113, 0.3);
}

.refund-metric-card--amount {
  border-color: rgba(60, 179, 113, 0.3);
  background: var(--admin-primary-soft);
}

.refund-metric-card__heading {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--admin-muted);
  font-size: 13px;
  font-weight: 700;
}

.refund-metric-card__icon {
  display: inline-grid;
  width: 30px;
  height: 30px;
  place-items: center;
  border-radius: 9px;
  background: var(--admin-primary-soft);
  color: var(--admin-primary-deep);
  font-size: 16px;
  font-weight: 800;
}

.refund-metric-card strong {
  color: var(--admin-ink);
  font-size: clamp(28px, 3vw, 38px);
  letter-spacing: -0.04em;
  line-height: 1;
}

.refund-metric-card small {
  color: var(--admin-muted);
  font-size: 12px;
}

.refund-filter-card {
  display: grid;
  align-content: center;
  gap: 14px;
}

.refund-filter-card__label {
  color: var(--admin-muted);
  font-size: 13px;
  font-weight: 700;
}

.refunds-control {
  display: grid;
  gap: 7px;
  color: var(--admin-muted);
  font-size: 13px;
  font-weight: 700;
}

.refunds-control input,
.refunds-control select {
  min-height: 42px;
  padding: 9px 12px;
  border: 1px solid var(--admin-outline);
  border-radius: 9px;
  background: var(--admin-surface-low);
  color: var(--admin-ink);
}

.refunds-control select {
  width: 100%;
}

.refunds-workspace {
  overflow: hidden;
  border: 1px solid var(--admin-outline-soft);
  border-radius: 16px;
  background: var(--admin-surface);
  box-shadow: 0 4px 20px rgba(28, 67, 46, 0.04);
}

.refunds-toolbar {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  padding: 24px 28px 20px;
  border-bottom: 1px solid var(--admin-outline-soft);
}

.refunds-toolbar h2 {
  margin: 0 0 6px;
  font-size: 22px;
  letter-spacing: -0.03em;
}

.refunds-search {
  min-width: min(360px, 100%);
}

.refunds-search input {
  width: 330px;
}

.refunds-feedback {
  margin: 16px 28px 0;
  padding: 12px 14px;
  border: 1px solid var(--admin-outline-soft);
  border-radius: 9px;
  color: var(--admin-muted);
  font-size: 13px;
  line-height: 1.5;
}

.refunds-feedback--success {
  border-color: rgba(60, 179, 113, 0.35);
  background: var(--admin-primary-soft);
  color: var(--admin-primary-deep);
}

.refunds-feedback--error {
  border-color: rgba(176, 64, 64, 0.3);
  background: #fff7f7;
  color: #9d3d3d;
}

.refunds-feedback--loading {
  margin-bottom: 16px;
  text-align: center;
}

.refunds-feedback button,
.refunds-empty button {
  min-height: 32px;
  margin-left: 10px;
  padding: 5px 12px;
  border: 1px solid rgba(36, 138, 85, 0.28);
  border-radius: 7px;
  background: var(--admin-primary-soft);
  color: var(--admin-primary-deep);
  font-size: 12px;
  font-weight: 700;
}

.refunds-table-wrap {
  overflow-x: auto;
}

.refunds-table {
  width: 100%;
  min-width: 1080px;
  border-collapse: collapse;
  text-align: left;
}

.refunds-table th,
.refunds-table td {
  padding: 15px 16px;
  border-bottom: 1px solid var(--admin-outline-soft);
  vertical-align: middle;
}

.refunds-table th {
  background: rgba(246, 243, 242, 0.62);
  color: var(--admin-muted);
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

.refunds-table td {
  color: var(--admin-ink);
  font-size: 13px;
}

.refund-identity-cell,
.refund-reason-cell {
  display: grid;
  gap: 6px;
  min-width: 210px;
}

.refund-identity-cell strong {
  overflow-wrap: anywhere;
  color: var(--admin-primary-deep);
  font-size: 13px;
}

.refund-identity-cell span {
  color: var(--admin-muted);
  font-size: 12px;
}

.refund-reason-cell span {
  max-width: 250px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.refund-reason-cell strong,
.refund-amount {
  color: #a23f3f;
  font-variant-numeric: tabular-nums;
  font-weight: 700;
  white-space: nowrap;
}

.refund-status {
  display: inline-flex;
  min-height: 30px;
  align-items: center;
  padding: 5px 10px;
  border: 1px solid var(--admin-outline-soft);
  border-radius: 999px;
  background: #f5f5f5;
  color: var(--admin-muted);
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

.refund-status--payment {
  margin-top: 6px;
}

.refund-status--applied,
.refund-status--processing {
  border-color: rgba(214, 154, 53, 0.28);
  background: #fff9eb;
  color: #9d6810;
}

.refund-status--approved {
  border-color: rgba(60, 179, 113, 0.3);
  background: var(--admin-primary-soft);
  color: var(--admin-primary-deep);
}

.refund-status--success {
  border-color: rgba(60, 179, 113, 0.3);
  background: var(--admin-primary-soft);
  color: var(--admin-primary-deep);
}

.refund-status--failed {
  border-color: rgba(176, 64, 64, 0.25);
  background: #fff7f7;
  color: #9d3d3d;
}

.refund-status--rejected {
  border-color: rgba(176, 64, 64, 0.25);
  background: #fff7f7;
  color: #9d3d3d;
}

.refund-actions {
  display: flex;
  min-width: 260px;
  flex-wrap: wrap;
  align-items: center;
  gap: 7px;
}

.refund-actions button {
  min-height: 34px;
  padding: 7px 10px;
  border: 1px solid rgba(36, 138, 85, 0.28);
  border-radius: 7px;
  background: var(--admin-primary-soft);
  color: var(--admin-primary-deep);
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

.refund-actions button:hover:not(:disabled) {
  border-color: rgba(36, 138, 85, 0.5);
  background: #e4f7eb;
}

.refund-actions button:disabled {
  cursor: not-allowed;
}

.refund-actions .refund-detail-button {
  border-color: var(--admin-outline);
  background: transparent;
  color: var(--admin-muted);
}

.refund-detail-row td {
  padding: 0;
  background: var(--admin-surface-low);
}

.refund-detail-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px 22px;
  margin: 0;
  padding: 18px 20px 20px;
}

.refund-detail-grid > div {
  min-width: 0;
}

.refund-detail-grid__wide {
  grid-column: span 2;
}

.refund-detail-grid dt {
  margin-bottom: 5px;
  color: var(--admin-muted);
  font-size: 12px;
  font-weight: 700;
}

.refund-detail-grid dd {
  margin: 0;
  overflow-wrap: anywhere;
  color: var(--admin-ink);
  font-size: 13px;
  line-height: 1.5;
}

.refunds-empty {
  padding: 48px 24px !important;
  color: var(--admin-muted) !important;
  text-align: center;
}

.refunds-empty strong {
  color: var(--admin-ink);
  font-size: 15px;
}

.refunds-empty p {
  margin: 8px 0 0;
  font-size: 13px;
}

.refunds-empty button {
  margin: 16px 0 0;
}

.refunds-table-footer {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 20px;
  color: var(--admin-muted);
  font-size: 12px;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

@media (max-width: 1180px) {
  .refunds-summary {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .refund-filter-card {
    min-height: 126px;
  }
}

@media (max-width: 900px) {
  .refunds-page {
    width: min(100% - 32px, 720px);
  }

  .refunds-header,
  .refunds-toolbar {
    align-items: stretch;
    flex-direction: column;
  }

  .refunds-summary {
    grid-template-columns: 1fr;
  }

  .refund-metric-card,
  .refund-filter-card {
    min-height: 132px;
  }

  .refunds-search input {
    width: 100%;
  }
}

@media (max-width: 640px) {
  .refunds-page {
    width: min(100% - 24px, 560px);
    padding-top: 24px;
  }

  .refunds-header h1 {
    font-size: 32px;
  }

  .refunds-toolbar,
  .refunds-table-footer {
    padding-right: 18px;
    padding-left: 18px;
  }

  .refunds-feedback {
    margin-right: 18px;
    margin-left: 18px;
  }

  .refund-detail-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .refund-detail-grid__wide {
    grid-column: 1 / -1;
  }

  .refunds-table-footer {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
